import { HttpMethods, sendRequest } from "../util/ApiHelper"

const CONTROLLER_NAME = "CycleCountGroups";

interface GetDataForCycleItemParams {
    batchId: number;
}

export const getDataForCycleItem = async (params: GetDataForCycleItemParams) => {
    return await sendRequest(
        HttpMethods.GET,
        `${CONTROLLER_NAME}/GetDataForCycleItem`,
        undefined,
        {
            batchId: String(params.batchId)
        }
    )
}

/**
 * Bug 900806: current server-side state for one or more cycle-count rows
 * (in_item_loc_bin_count), so a queued or on-screen card can stop echoing the
 * values it captured when the screen opened / the batch was downloaded.
 *
 * RowVersion is the optimistic-concurrency token. Variance has to come along
 * with it: `variance` is a DB computed column (counted_qty - system_qty) that the
 * app's schema metadata does NOT mark read-only, so if the value we post
 * disagrees with the stored one the ORM flags it as changed and SQL Server
 * rejects the whole update with "The column \"variance\" cannot be modified
 * because it is either a computed column or is the result of a UNION operator."
 * Omitting it does not help - it then binds as 0 and still disagrees.
 *
 * Uses GetLiteList rather than the per-row Get(countId) overload: BaseApiController
 * routes as api/[controller]/[action] and both Get overloads share the "Get" action
 * name, so that endpoint is an ambiguous match and is not callable. GetLiteList
 * copies caller-named properties straight off the data object, keeping the payload
 * to just the key plus the fields we ask for.
 */
export interface GroupCountRowState {
    RowVersion: any;
    Variance: any;
}

export const getGroupCountRowState = async (countIds: number[]) => {
    return await sendRequest(
        HttpMethods.POST,
        `${CONTROLLER_NAME}/GetLiteList`,
        {
            Filter: `count_id in (${countIds.join(",")})`,
            PropertyList: ["RowVersion", "Variance"],
        }
    )
}

/**
 * Maps a GetLiteList response into { [CountId]: { RowVersion, Variance } }.
 * Rows without a usable RowVersion are skipped so callers never overwrite a good
 * token with nothing.
 */
export const toRowStateMap = (data: any): Record<number, GroupCountRowState> => {
    const map: Record<number, GroupCountRowState> = {};
    if (Array.isArray(data)) {
        data.forEach((row: any) => {
            const rowVersion = row?.Properties?.RowVersion;
            if (row?.CountId != null && rowVersion) {
                map[Number(row.CountId)] = {
                    RowVersion: rowVersion,
                    Variance: row?.Properties?.Variance,
                };
            }
        });
    }
    return map;
}
